import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  AdminApi,
  AdminQuestionGroup,
  AdminQuestionListItem,
  AdminQuestionTopic,
  AdminTestObject,
  AdminTestPaperQuestionInput,
} from '../../services/adminApi'

type SelectedQuestion = {
  questionId: number
  preview: string
  questionType: string
  groupId: number | null
  point: number
}

const TYPE_LABEL: Record<string, string> = {
  SINGLE_CHOICE: '1 đáp án',
  CHOICE: '1 đáp án',
  MULTIPLE_CHOICE: 'Nhiều đáp án',
  ESSAY: 'Tự luận',
}

const AdminTestComposePage = () => {
  const { paperId: paperIdParam } = useParams()
  const navigate = useNavigate()
  const isEdit = Boolean(paperIdParam)
  const paperId = paperIdParam ? Number(paperIdParam) : null

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [objects, setObjects] = useState<AdminTestObject[]>([])
  const [topics, setTopics] = useState<AdminQuestionTopic[]>([])
  const [groups, setGroups] = useState<AdminQuestionGroup[]>([])

  // Paper metadata
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [minute, setMinute] = useState(60)
  const [maxTime, setMaxTime] = useState(1)
  const [passPercent, setPassPercent] = useState(50)
  const [objectId, setObjectId] = useState<number | ''>('')
  const [isActive, setIsActive] = useState(1)
  const [isPublic, setIsPublic] = useState(0)
  const [needApproveResult, setNeedApproveResult] = useState(0)
  const [allowPause, setAllowPause] = useState(0)

  // Selected questions
  const [selected, setSelected] = useState<SelectedQuestion[]>([])

  // Picker
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerLoading, setPickerLoading] = useState(false)
  const [pickerItems, setPickerItems] = useState<AdminQuestionListItem[]>([])
  const [pickerPage, setPickerPage] = useState(1)
  const [pickerTotalPages, setPickerTotalPages] = useState(0)
  const [pq, setPq] = useState('')
  const [pType, setPType] = useState('')
  const [pLevel, setPLevel] = useState('')
  const [pTopicId, setPTopicId] = useState<number | undefined>()
  const [pGroupId, setPGroupId] = useState<number | undefined>()

  const excludeIds = useMemo(() => selected.map((s) => s.questionId), [selected])
  const totalPoint = useMemo(
    () => selected.reduce((sum, s) => sum + (Number(s.point) || 0), 0),
    [selected]
  )

  useEffect(() => {
    ;(async () => {
      try {
        const [oRes, tRes, gRes] = await Promise.all([
          AdminApi.listTestObjects(),
          AdminApi.listQuestionTopics(),
          AdminApi.listQuestionGroups({ limit: 100, isActive: 1 }),
        ])
        setObjects(oRes.data || [])
        setTopics(tRes.data || [])
        setGroups(gRes.data?.items || [])
      } catch {
        toast.error('Không tải được metadata')
      }
    })()
  }, [])

  useEffect(() => {
    if (!isEdit || !paperId) return
    ;(async () => {
      setLoading(true)
      try {
        const res = await AdminApi.getAdminTestDetails(paperId)
        const paper = res.data
        setName(paper.name || '')
        setDescription(paper.description || '')
        setMinute(paper.minute ?? 60)
        setMaxTime(paper.max_time ?? paper.maxTime ?? 1)
        setPassPercent(paper.pass_percent ?? paper.passPercent ?? 50)
        setObjectId(paper.object_id ?? paper.objectId ?? '')
        setIsActive(paper.is_active ?? paper.isActive ?? 1)
        setIsPublic(paper.is_public ?? paper.isPublic ?? 0)
        setNeedApproveResult(paper.need_approve_result ?? paper.needApproveResult ?? 0)
        setAllowPause(paper.allow_pause ?? paper.allowPause ?? 0)

        const qs = Array.isArray(paper.questions) ? paper.questions : []
        setSelected(
          qs.map((pqItem: any) => {
            const qd = pqItem.questionData || {}
            return {
              questionId: pqItem.question_id ?? pqItem.questionId,
              preview: String(qd.content || '')
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .slice(0, 120),
              questionType: qd.question_type || qd.questionType || 'CHOICE',
              groupId: pqItem.group_id ?? pqItem.groupId ?? qd.group_id ?? null,
              point: Number(pqItem.point ?? qd.point ?? 1),
            }
          })
        )
      } catch {
        toast.error('Không tải được bài test')
        navigate('/admin/tests')
      } finally {
        setLoading(false)
      }
    })()
  }, [isEdit, paperId, navigate])

  const loadPicker = async (overrides?: { page?: number }) => {
    const nextPage = overrides?.page ?? pickerPage
    setPickerLoading(true)
    try {
      const res = await AdminApi.listQuestions({
        q: pq.trim() || undefined,
        questionType: pType || undefined,
        level: pLevel || undefined,
        topicId: pTopicId,
        groupId: pGroupId,
        isActive: 1,
        excludeIds,
        page: nextPage,
        limit: 15,
      })
      setPickerItems(res.data.items)
      setPickerTotalPages(res.data.pagination.totalPages)
      setPickerPage(nextPage)
    } catch {
      toast.error('Không tải được danh sách câu hỏi')
    } finally {
      setPickerLoading(false)
    }
  }

  useEffect(() => {
    if (pickerOpen) void loadPicker({ page: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen])

  const addQuestion = (item: AdminQuestionListItem) => {
    if (selected.some((s) => s.questionId === item.id)) return
    setSelected((prev) => [
      ...prev,
      {
        questionId: item.id,
        preview: item.preview || `#${item.id}`,
        questionType: item.questionType,
        groupId: item.groupId ?? null,
        point: item.point != null ? Number(item.point) : 1,
      },
    ])
  }

  const addGroupQuestions = async (gid: number) => {
    try {
      const res = await AdminApi.listQuestions({
        groupId: gid,
        isActive: 1,
        limit: 100,
      })
      const toAdd = res.data.items.filter(
        (q) => !selected.some((s) => s.questionId === q.id)
      )
      if (toAdd.length === 0) {
        toast.info('Không còn câu mới trong nhóm')
        return
      }
      setSelected((prev) => [
        ...prev,
        ...toAdd.map((item) => ({
          questionId: item.id,
          preview: item.preview || `#${item.id}`,
          questionType: item.questionType,
          groupId: item.groupId ?? gid,
          point: item.point != null ? Number(item.point) : 1,
        })),
      ])
      toast.success(`Đã thêm ${toAdd.length} câu từ nhóm #${gid}`)
    } catch {
      toast.error('Không thêm được câu từ nhóm')
    }
  }

  const move = (index: number, dir: -1 | 1) => {
    setSelected((prev) => {
      const next = [...prev]
      const j = index + dir
      if (j < 0 || j >= next.length) return prev
      ;[next[index], next[j]] = [next[j], next[index]]
      return next
    })
  }

  const save = async () => {
    if (!name.trim()) {
      toast.error('Nhập tên bài test')
      return
    }
    setSaving(true)
    try {
      const meta = {
        name: name.trim(),
        description: description || null,
        minute,
        maxTime,
        passPercent,
        objectId: objectId === '' ? null : Number(objectId),
        isActive,
        isPublic,
        needApproveResult,
        allowPause,
      }

      let targetId = paperId
      if (isEdit && paperId) {
        const u = await AdminApi.updateTestPaper(paperId, meta)
        if (!u.success) {
          toast.error(u.message || 'Cập nhật metadata thất bại')
          return
        }
      } else {
        const c = await AdminApi.createTestPaper(meta)
        if (!c.success || !c.data?.id) {
          toast.error(c.message || 'Tạo bài test thất bại')
          return
        }
        targetId = c.data.id
      }

      const questions: AdminTestPaperQuestionInput[] = selected.map((s, i) => ({
        questionId: s.questionId,
        point: s.point,
        sort: i + 1,
        groupId: s.groupId,
      }))

      const qRes = await AdminApi.setTestPaperQuestions(targetId!, questions)
      if (!qRes.success) {
        toast.error(qRes.message || 'Lưu danh sách câu hỏi thất bại')
        return
      }

      toast.success('Đã lưu bài test')
      navigate(`/admin/tests/${targetId}`)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Lưu thất bại'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <p className="text-sm text-m2-on-surface-variant">Đang tải…</p>
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/tests" className="text-sm text-m2-primary hover:underline">
            ← Quản lý bài test
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-m2-on-surface">
            {isEdit ? `Soạn đề #${paperId}` : 'Tạo bài test'}
          </h1>
          <p className="mt-1 text-sm text-m2-on-surface-variant">
            {selected.length} câu · {totalPoint} điểm tối đa
          </p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className="rounded-lg bg-m2-primary px-5 py-2 text-sm font-semibold text-m2-on-primary disabled:opacity-50"
        >
          {saving ? 'Đang lưu…' : 'Lưu bài test'}
        </button>
      </div>

      <div className="grid gap-4 rounded-xl border border-m2-outline-variant bg-m2-surface-container p-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="mb-1 block text-xs font-semibold">Tên bài test</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="mb-1 block text-xs font-semibold">Mô tả</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Thời gian (phút)</label>
          <input
            type="number"
            min={1}
            value={minute}
            onChange={(e) => setMinute(Number(e.target.value))}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Số lần làm (max_time)</label>
          <input
            type="number"
            min={1}
            value={maxTime}
            onChange={(e) => setMaxTime(Number(e.target.value))}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">% đạt</label>
          <input
            type="number"
            min={0}
            max={100}
            value={passPercent}
            onChange={(e) => setPassPercent(Number(e.target.value))}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Đối tượng</label>
          <select
            value={objectId}
            onChange={(e) => setObjectId(e.target.value ? Number(e.target.value) : '')}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          >
            <option value="">—</option>
            {objects.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Active</label>
          <select
            value={isActive}
            onChange={(e) => setIsActive(Number(e.target.value))}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          >
            <option value={1}>Có</option>
            <option value={0}>Không</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Public</label>
          <select
            value={isPublic}
            onChange={(e) => setIsPublic(Number(e.target.value))}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          >
            <option value={0}>Không</option>
            <option value={1}>Có</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Cần duyệt kết quả</label>
          <select
            value={needApproveResult}
            onChange={(e) => setNeedApproveResult(Number(e.target.value))}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          >
            <option value={0}>Không</option>
            <option value={1}>Có</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Cho phép tạm dừng</label>
          <select
            value={allowPause}
            onChange={(e) => setAllowPause(Number(e.target.value))}
            className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-2 text-sm"
          >
            <option value={0}>Không</option>
            <option value={1}>Có</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-m2-outline-variant bg-m2-surface-container p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Câu hỏi trong đề</h2>
          <div className="flex flex-wrap gap-2">
            <select
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value
                e.target.value = ''
                if (v) void addGroupQuestions(Number(v))
              }}
              className="rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-1.5 text-xs"
            >
              <option value="">+ Thêm cả nhóm…</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  #{g.id} — {g.preview?.slice(0, 40) || 'Nhóm'}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="rounded-lg bg-m2-primary px-3 py-1.5 text-xs font-semibold text-m2-on-primary"
            >
              Chọn từ ngân hàng
            </button>
          </div>
        </div>

        {selected.length === 0 ? (
          <p className="py-6 text-center text-sm text-m2-on-surface-variant">
            Chưa có câu hỏi. Hãy lọc và chọn từ ngân hàng.
          </p>
        ) : (
          <ul className="space-y-2">
            {selected.map((s, index) => (
              <li
                key={s.questionId}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-m2-outline-variant/70 bg-m2-surface-low px-3 py-2 text-sm"
              >
                <span className="w-8 font-mono text-xs text-m2-on-surface-variant">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2">
                    <span className="font-mono text-xs text-m2-on-surface-variant">
                      #{s.questionId}
                    </span>{' '}
                    {s.preview}
                  </p>
                  <p className="text-xs text-m2-on-surface-variant">
                    {TYPE_LABEL[String(s.questionType).toUpperCase()] || s.questionType}
                    {s.groupId ? ` · Nhóm #${s.groupId}` : ''}
                  </p>
                </div>
                <label className="flex items-center gap-1 text-xs">
                  Điểm
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={s.point}
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      setSelected((prev) =>
                        prev.map((x, i) => (i === index ? { ...x, point: v } : x))
                      )
                    }}
                    className="w-16 rounded border border-m2-outline-variant bg-m2-surface-container px-2 py-1"
                  />
                </label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="rounded border border-m2-outline-variant px-2 py-1 text-xs disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === selected.length - 1}
                    className="rounded border border-m2-outline-variant px-2 py-1 text-xs disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setSelected((prev) => prev.filter((_, i) => i !== index))
                    }
                    className="rounded border border-red-200 px-2 py-1 text-xs text-red-600"
                  >
                    Xóa
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-m2-outline-variant bg-m2-surface-container shadow-xl">
            <div className="flex items-center justify-between border-b border-m2-outline-variant px-4 py-3">
              <h3 className="font-semibold">Chọn câu hỏi từ ngân hàng</h3>
              <button
                type="button"
                onClick={() => setPickerOpen(false)}
                className="text-sm text-m2-on-surface-variant hover:underline"
              >
                Đóng
              </button>
            </div>
            <div className="flex flex-wrap gap-2 border-b border-m2-outline-variant p-3">
              <input
                type="text"
                value={pq}
                onChange={(e) => setPq(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void loadPicker({ page: 1 })}
                placeholder="Tìm…"
                className="min-w-[140px] flex-1 rounded-lg border border-m2-outline-variant bg-m2-surface-low px-3 py-1.5 text-sm"
              />
              <select
                value={pType}
                onChange={(e) => setPType(e.target.value)}
                className="rounded-lg border border-m2-outline-variant bg-m2-surface-low px-2 py-1.5 text-sm"
              >
                <option value="">Loại</option>
                <option value="SINGLE_CHOICE">1 đáp án</option>
                <option value="MULTIPLE_CHOICE">Nhiều đáp án</option>
                <option value="ESSAY">Tự luận</option>
              </select>
              <select
                value={pLevel}
                onChange={(e) => setPLevel(e.target.value)}
                className="rounded-lg border border-m2-outline-variant bg-m2-surface-low px-2 py-1.5 text-sm"
              >
                <option value="">Độ khó</option>
                <option value="EASY">EASY</option>
                <option value="MEDIUM">MEDIUM</option>
                <option value="HARD">HARD</option>
              </select>
              <select
                value={pTopicId ?? ''}
                onChange={(e) =>
                  setPTopicId(e.target.value ? Number(e.target.value) : undefined)
                }
                className="rounded-lg border border-m2-outline-variant bg-m2-surface-low px-2 py-1.5 text-sm"
              >
                <option value="">Chủ đề</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <select
                value={pGroupId ?? ''}
                onChange={(e) =>
                  setPGroupId(e.target.value ? Number(e.target.value) : undefined)
                }
                className="rounded-lg border border-m2-outline-variant bg-m2-surface-low px-2 py-1.5 text-sm"
              >
                <option value="">Nhóm</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    #{g.id}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => void loadPicker({ page: 1 })}
                className="rounded-lg bg-m2-primary px-3 py-1.5 text-sm font-semibold text-m2-on-primary"
              >
                Lọc
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {pickerLoading ? (
                <p className="py-6 text-center text-sm text-m2-on-surface-variant">Đang tải…</p>
              ) : pickerItems.length === 0 ? (
                <p className="py-6 text-center text-sm text-m2-on-surface-variant">
                  Không còn câu hỏi phù hợp
                </p>
              ) : (
                <ul className="space-y-2">
                  {pickerItems.map((item) => {
                    const already = selected.some((s) => s.questionId === item.id)
                    return (
                      <li
                        key={item.id}
                        className="flex items-start justify-between gap-2 rounded-lg border border-m2-outline-variant/60 bg-m2-surface-low px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <p className="line-clamp-2">
                            <span className="font-mono text-xs">#{item.id}</span> {item.preview}
                          </p>
                          <p className="text-xs text-m2-on-surface-variant">
                            {TYPE_LABEL[String(item.questionType).toUpperCase()] ||
                              item.questionType}{' '}
                            · {item.level}
                            {item.groupId ? ` · Nhóm #${item.groupId}` : ''}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={already}
                          onClick={() => {
                            addQuestion(item)
                            void loadPicker({ page: pickerPage })
                          }}
                          className="shrink-0 rounded-lg border border-m2-outline-variant px-3 py-1 text-xs font-semibold disabled:opacity-40"
                        >
                          {already ? 'Đã chọn' : 'Thêm'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            {pickerTotalPages > 1 && (
              <div className="flex items-center justify-between border-t border-m2-outline-variant px-4 py-2 text-sm">
                <button
                  type="button"
                  disabled={pickerPage <= 1}
                  onClick={() => void loadPicker({ page: pickerPage - 1 })}
                  className="rounded border border-m2-outline-variant px-2 py-1 disabled:opacity-40"
                >
                  Trước
                </button>
                <span>
                  {pickerPage}/{pickerTotalPages}
                </span>
                <button
                  type="button"
                  disabled={pickerPage >= pickerTotalPages}
                  onClick={() => void loadPicker({ page: pickerPage + 1 })}
                  className="rounded border border-m2-outline-variant px-2 py-1 disabled:opacity-40"
                >
                  Sau
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminTestComposePage
